import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { ClerkProvider } from '@clerk/clerk-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { PageLoadingFallback } from '@/components/ui/PageLoadingFallback'
import { LazyRouteErrorBoundary } from '@/components/ui/LazyRouteErrorBoundary'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AuthTokenProvider } from '@/components/auth/AuthTokenProvider'
import { PostLoginRouter } from '@/components/auth/PostLoginRouter'
import { AgencyProvider } from '@/contexts/AgencyContext'
import { BrandProvider } from '@/contexts/BrandContext'
import { WebSocketProvider } from '@/contexts/WebSocketContext'
import { FeatureFlagsProvider } from '@/context/FeatureFlagsContext'

// Layout components — eagerly loaded (needed for app shell)
import { AppLayout } from '@/components/layout/AppLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'

// Eagerly loaded pages — landing/marketing (small, needed for SEO/FCP)
import { LandingPage } from '@/pages/LandingPage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { NotFoundPage } from '@/pages/NotFoundPage'

// ---------------------------------------------------------------------------
// Lazy-loaded page components — grouped by feature domain
// ---------------------------------------------------------------------------

// Dashboard
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })))
const ConsolidatedDashboard = lazy(() => import('@/components/dashboard/ConsolidatedDashboard'))
const BrandDashboardWrapper = lazy(() => import('@/components/dashboard/BrandDashboardWrapper'))

// Agents
const AgentsPage = lazy(() => import('@/pages/agents/AgentsPage').then(m => ({ default: m.AgentsPage })))
const AgentsPageDark = lazy(() => import('@/pages/agents/AgentsPageDark').then(m => ({ default: m.AgentsPageDark })))
const AgentDetailPage = lazy(() => import('@/pages/agents/AgentDetailPage').then(m => ({ default: m.AgentDetailPage })))
const TaskDetailPage = lazy(() => import('@/pages/agents/TaskDetailPage').then(m => ({ default: m.TaskDetailPage })))
const AgentComparisonPage = lazy(() => import('@/pages/agents/AgentComparisonPage').then(m => ({ default: m.AgentComparisonPage })))

// Intelligence
const IntelligenceDashboardPage = lazy(() => import('@/pages/intelligence/IntelligenceDashboardPage').then(m => ({ default: m.IntelligenceDashboardPage })))
const AlertsManagementPage = lazy(() => import('@/pages/intelligence/AlertsManagementPage').then(m => ({ default: m.AlertsManagementPage })))
const AnalyticsDashboardPage = lazy(() => import('@/pages/intelligence/AnalyticsDashboardPage').then(m => ({ default: m.AnalyticsDashboardPage })))
const ComparisonPage = lazy(() => import('@/pages/intelligence/ComparisonPage').then(m => ({ default: m.ComparisonPage })))

// Settings
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))

// Analytics
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })))
const ComparativeAnalyticsPage = lazy(() => import('@/pages/analytics/ComparativeAnalyticsPage'))
const CostAnalyticsPage = lazy(() => import('@/pages/analytics/CostAnalyticsPage'))

// Campaigns
const CampaignsPage = lazy(() => import('@/pages/campaigns/CampaignsPage').then(m => ({ default: m.CampaignsPage })))
const NewCampaignPage = lazy(() => import('@/pages/campaigns/NewCampaignPage').then(m => ({ default: m.NewCampaignPage })))
const CampaignDetailPage = lazy(() => import('@/pages/campaigns/CampaignDetailPage').then(m => ({ default: m.CampaignDetailPage })))
const CampaignAnalyticsPage = lazy(() => import('@/pages/campaigns/CampaignAnalyticsPage').then(m => ({ default: m.CampaignAnalyticsPage })))
const CampaignExecutionPage = lazy(() => import('@/pages/campaigns/CampaignExecutionPage').then(m => ({ default: m.CampaignExecutionPage })))
const CampaignHealthPage = lazy(() => import('@/pages/campaigns/CampaignHealthPage').then(m => ({ default: m.CampaignHealthPage })))
const TemplatesGalleryPage = lazy(() => import('@/pages/campaigns/TemplatesGalleryPage').then(m => ({ default: m.TemplatesGalleryPage })))
const MonitoringDashboardPage = lazy(() => import('@/pages/campaigns/MonitoringDashboardPage').then(m => ({ default: m.MonitoringDashboardPage })))
const AdvancedAnalyticsPage = lazy(() => import('@/pages/campaigns/AdvancedAnalyticsPage').then(m => ({ default: m.AdvancedAnalyticsPage })))
const CampaignTimelinePage = lazy(() => import('@/pages/campaigns/CampaignTimelinePage'))
const CampaignHistoryPage = lazy(() => import('@/pages/campaigns/CampaignHistoryPage').then(m => ({ default: m.CampaignHistoryPage })))

// Templates
const TemplatesPage = lazy(() => import('@/pages/templates/TemplatesPage').then(m => ({ default: m.TemplatesPage })))
const TemplateDetailPage = lazy(() => import('@/pages/templates/TemplateDetailPage').then(m => ({ default: m.TemplateDetailPage })))
const CustomizeTemplatePage = lazy(() => import('@/pages/templates/CustomizeTemplatePage').then(m => ({ default: m.CustomizeTemplatePage })))

// Brands
const BrandsPage = lazy(() => import('@/pages/brands/BrandsPage').then(m => ({ default: m.BrandsPage })))
const NewBrandPage = lazy(() => import('@/pages/brands/NewBrandPage').then(m => ({ default: m.NewBrandPage })))
const BrandDetailPage = lazy(() => import('@/pages/brands/BrandDetailPage').then(m => ({ default: m.BrandDetailPage })))
const BrandComparisonPage = lazy(() => import('@/pages/brands/BrandComparisonPage').then(m => ({ default: m.BrandComparisonPage })))
const BrandSelectorPage = lazy(() => import('@/pages/brands/BrandSelectorPage').then(m => ({ default: m.BrandSelectorPage })))
const MultiBrandDashboardPage = lazy(() => import('@/pages/multi-brand/MultiBrandDashboardPage'))

// Competitive
const CompetitorsPage = lazy(() => import('@/pages/competitive/CompetitorsPage').then(m => ({ default: m.CompetitorsPage })))
const CompetitorDetailPage = lazy(() => import('@/pages/competitive/CompetitorDetailPage').then(m => ({ default: m.CompetitorDetailPage })))
const CompetitiveIntelPage = lazy(() => import('@/pages/competitive/CompetitiveIntelPage').then(m => ({ default: m.CompetitiveIntelPage })))

// Briefs
const BriefsListPage = lazy(() => import('@/pages/briefs/BriefsListPage'))
const NewBriefPage = lazy(() => import('@/pages/briefs/NewBriefPage'))
const EditBriefPage = lazy(() => import('@/pages/briefs/EditBriefPage'))
const BriefDetailPage = lazy(() => import('@/pages/briefs/BriefDetailPage'))

// A/B Tests
const ABTestsPage = lazy(() => import('@/pages/abtests/ABTestsPage').then(m => ({ default: m.ABTestsPage })))
const ABTestDetailPage = lazy(() => import('@/pages/abtests/ABTestDetailPage').then(m => ({ default: m.ABTestDetailPage })))

// Reports
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'))

// Integrations
const IntegrationsPage = lazy(() => import('@/pages/integrations/IntegrationsPage').then(m => ({ default: m.IntegrationsPage })))
const AddIntegrationPage = lazy(() => import('@/pages/integrations/AddIntegrationPage').then(m => ({ default: m.AddIntegrationPage })))
const IntegrationDetailPage = lazy(() => import('@/pages/integrations/IntegrationDetailPage').then(m => ({ default: m.IntegrationDetailPage })))

// Decisions & Workflows
const DecisionsPage = lazy(() => import('@/pages/decisions/DecisionsPage').then(m => ({ default: m.DecisionsPage })))
const DecisionDetailPage = lazy(() => import('@/pages/decisions/DecisionDetailPage').then(m => ({ default: m.DecisionDetailPage })))
const WorkflowsPage = lazy(() => import('@/pages/workflows/WorkflowsPage').then(m => ({ default: m.WorkflowsPage })))

// Creatives
const CreativesPage = lazy(() => import('@/pages/creatives/CreativesPage').then(m => ({ default: m.CreativesPage })))
const CreativeDetailPage = lazy(() => import('@/pages/creatives/CreativeDetailPage').then(m => ({ default: m.CreativeDetailPage })))
const CreativeAnalyticsPage = lazy(() => import('@/pages/creatives/analytics/CreativeAnalyticsPage').then(m => ({ default: m.CreativeAnalyticsPage })))

// Customers & Segments
const CustomersPage = lazy(() => import('@/pages/customers/CustomersPage').then(m => ({ default: m.CustomersPage })))
const Customer360Page = lazy(() => import('@/pages/customers/Customer360Page').then(m => ({ default: m.Customer360Page })))
const SegmentsPage = lazy(() => import('@/pages/segments/SegmentsPage').then(m => ({ default: m.SegmentsPage })))
const SegmentDetailPage = lazy(() => import('@/pages/segments/SegmentDetailPage').then(m => ({ default: m.SegmentDetailPage })))

// Content
const ContentLibraryPage = lazy(() => import('@/pages/content/ContentLibraryPage').then(m => ({ default: m.ContentLibraryPage })))

// Admin
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })))
const FeedbackManagementPage = lazy(() => import('@/pages/admin/FeedbackManagementPage').then(m => ({ default: m.FeedbackManagementPage })))

// Public pages — lazy loaded (not critical for initial paint)
const StatusPage = lazy(() => import('@/pages/status/StatusPage'))
const PricingPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.PricingPage })))
const EnterpriseSecurityPage = lazy(() => import('@/pages/EnterpriseSecurityPage').then(m => ({ default: m.EnterpriseSecurityPage })))
const DemoPage = lazy(() => import('@/pages/DemoPage').then(m => ({ default: m.DemoPage })))
const GlassComponentsDemo = lazy(() => import('@/pages/test/GlassComponentsDemo').then(m => ({ default: m.GlassComponentsDemo })))
const LOILandingPage = lazy(() => import('@/pages/LOILandingPage'))
const AgenciesLandingPage = lazy(() => import('@/pages/AgenciesLandingPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const TermsPage = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })))
const ModalLLMEvaluatorPage = lazy(() => import('@/pages/ModalLLMEvaluatorPage').then(m => ({ default: m.ModalLLMEvaluatorPage })))

// Blog
const BlogPage = lazy(() => import('@/pages/blog/BlogPage').then(m => ({ default: m.BlogPage })))
const BlogPostPage = lazy(() => import('@/pages/blog/BlogPostPage').then(m => ({ default: m.BlogPostPage })))

// Partners
const PartnerApplyPage = lazy(() => import('@/pages/partners').then(m => ({ default: m.PartnerApplyPage })))
const PartnerDashboardPage = lazy(() => import('@/pages/partners').then(m => ({ default: m.PartnerDashboardPage })))

// Auth (non-critical)
const AuthDebugPage = lazy(() => import('@/pages/auth/AuthDebugPage').then(m => ({ default: m.AuthDebugPage })))

// Checkout
const CheckoutSuccessPage = lazy(() => import('@/pages/checkout').then(m => ({ default: m.CheckoutSuccessPage })))
const CheckoutCancelPage = lazy(() => import('@/pages/checkout').then(m => ({ default: m.CheckoutCancelPage })))
const UpgradeSuccessPage = lazy(() => import('@/pages/checkout').then(m => ({ default: m.UpgradeSuccessPage })))

// Onboarding
const OnboardingWizardPage = lazy(() => import('@/pages/onboarding').then(m => ({ default: m.OnboardingWizardPage })))

// Approvals
const ApprovalQueue = lazy(() => import('@/pages/ApprovalQueue'))


/** Wraps lazy-loaded route components with Suspense + error boundary */
function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <LazyRouteErrorBoundary>
      <Suspense fallback={<PageLoadingFallback />}>
        {children}
      </Suspense>
    </LazyRouteErrorBoundary>
  )
}

/** Authenticated app shell — ClerkProvider wraps only auth-dependent routes */
function AuthenticatedApp() {
  return (
    <ClerkProvider publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}>
      <AuthTokenProvider>
        <AgencyProvider>
          <BrandProvider>
            <WebSocketProvider>
              <FeatureFlagsProvider>
                <Outlet />
              </FeatureFlagsProvider>
            </WebSocketProvider>
          </BrandProvider>
        </AgencyProvider>
      </AuthTokenProvider>
    </ClerkProvider>
  )
}

function App() {
  return (
    <Routes>
      {/* ============================================ */}
      {/* PUBLIC ROUTES — no ClerkProvider needed      */}
      {/* ============================================ */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/status" element={<LazyRoute><StatusPage /></LazyRoute>} />
      <Route path="/pricing" element={<LazyRoute><PricingPage /></LazyRoute>} />
      <Route path="/enterprise-security" element={<LazyRoute><EnterpriseSecurityPage /></LazyRoute>} />
      <Route path="/demo" element={<LazyRoute><DemoPage /></LazyRoute>} />
      <Route path="/test/glass" element={<LazyRoute><GlassComponentsDemo /></LazyRoute>} />
      <Route path="/value" element={<LazyRoute><LOILandingPage /></LazyRoute>} />
      <Route path="/agencies" element={<LazyRoute><AgenciesLandingPage /></LazyRoute>} />
      <Route path="/products/llm-evaluator" element={<LazyRoute><ModalLLMEvaluatorPage /></LazyRoute>} />
      <Route path="/privacy" element={<LazyRoute><PrivacyPage /></LazyRoute>} />
      <Route path="/terms" element={<LazyRoute><TermsPage /></LazyRoute>} />
      <Route path="/blog" element={<LazyRoute><BlogPage /></LazyRoute>} />
      <Route path="/blog/:slug" element={<LazyRoute><BlogPostPage /></LazyRoute>} />
      <Route path="/partners/apply" element={<LazyRoute><PartnerApplyPage /></LazyRoute>} />
      <Route path="/auth-debug" element={<LazyRoute><AuthDebugPage /></LazyRoute>} />

      {/* ============================================ */}
      {/* AUTH ROUTES — ClerkProvider wraps these      */}
      {/* ============================================ */}
      <Route element={<AuthenticatedApp />}>
        {/* Auth pages */}
        <Route path="/auth/*" element={<AuthLayout />}>
          <Route path="login/*" element={<LoginPage />} />
          <Route path="register/*" element={<RegisterPage />} />
        </Route>

        {/* Checkout pages */}
        <Route path="/checkout/success" element={<LazyRoute><CheckoutSuccessPage /></LazyRoute>} />
        <Route path="/checkout/cancel" element={<LazyRoute><CheckoutCancelPage /></LazyRoute>} />
        <Route path="/billing/upgrade-success" element={<LazyRoute><UpgradeSuccessPage /></LazyRoute>} />

        {/* Onboarding */}
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <LazyRoute><OnboardingWizardPage /></LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/select-brand" element={
          <ProtectedRoute>
            <LazyRoute><BrandSelectorPage /></LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/welcome" element={
          <ProtectedRoute>
            <PostLoginRouter />
          </ProtectedRoute>
        } />

        {/* Application routes - Protected for authenticated users */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
        {/* All app routes are lazy-loaded with Suspense + error boundary */}
        <Route path="dashboard" element={<LazyRoute><DashboardPage /></LazyRoute>} />
        <Route path="dashboard/:brandId" element={<LazyRoute><ConsolidatedDashboard /></LazyRoute>} />
        <Route path="agents" element={<LazyRoute><AgentsPage /></LazyRoute>} />
        <Route path="agents/compare" element={<LazyRoute><AgentComparisonPage /></LazyRoute>} />
        <Route path="agents-dark" element={<LazyRoute><AgentsPageDark /></LazyRoute>} />
        <Route path="agents/:agentId" element={<LazyRoute><AgentDetailPage /></LazyRoute>} />
        <Route path="agents/tasks/:taskId" element={<LazyRoute><TaskDetailPage /></LazyRoute>} />
        <Route path="approvals" element={<LazyRoute><ApprovalQueue /></LazyRoute>} />
        <Route path="partners/dashboard" element={<LazyRoute><PartnerDashboardPage /></LazyRoute>} />
        <Route path="intelligence/dashboard" element={<LazyRoute><IntelligenceDashboardPage /></LazyRoute>} />
        <Route path="intelligence/alerts" element={<LazyRoute><AlertsManagementPage /></LazyRoute>} />
        <Route path="intelligence/analytics" element={<LazyRoute><AnalyticsDashboardPage /></LazyRoute>} />
        <Route path="intelligence/compare" element={<LazyRoute><ComparisonPage /></LazyRoute>} />
        {/* Redirect AI Intelligence and orchestration pages to Agents tabs */}
        <Route path="orchestration" element={<Navigate to="/agents?tab=orchestration" replace />} />
        <Route path="executions" element={<Navigate to="/agents?tab=executions" replace />} />
        <Route path="conflicts" element={<Navigate to="/agents?tab=conflicts" replace />} />
        <Route path="realtime" element={<Navigate to="/agents?tab=realtime" replace />} />
        <Route path="ml-predictions" element={<Navigate to="/agents?tab=ml-predictions" replace />} />
        <Route path="recommendations" element={<Navigate to="/agents?tab=recommendations" replace />} />
        <Route path="integrations" element={<LazyRoute><IntegrationsPage /></LazyRoute>} />
        <Route path="integrations/new" element={<LazyRoute><AddIntegrationPage /></LazyRoute>} />
        <Route path="integrations/:integrationId" element={<LazyRoute><IntegrationDetailPage /></LazyRoute>} />
        <Route path="analytics" element={<LazyRoute><AnalyticsPage /></LazyRoute>} />
        <Route path="cost-analytics" element={<LazyRoute><CostAnalyticsPage /></LazyRoute>} />
        {/* Redirect budget and attribution to Analytics tabs */}
        <Route path="budget" element={<Navigate to="/analytics?tab=budget" replace />} />
        <Route path="attribution" element={<Navigate to="/analytics?tab=attribution" replace />} />
        <Route path="campaigns" element={<LazyRoute><CampaignsPage /></LazyRoute>} />
        <Route path="campaigns/new" element={<LazyRoute><NewCampaignPage /></LazyRoute>} />
        <Route path="campaigns/templates" element={<LazyRoute><TemplatesGalleryPage /></LazyRoute>} />
        <Route path="campaigns/monitor" element={<LazyRoute><MonitoringDashboardPage /></LazyRoute>} />
        <Route path="campaigns/reports" element={<Navigate to="/analytics?tab=reports" replace />} />
        <Route path="campaigns/timeline" element={<LazyRoute><CampaignTimelinePage /></LazyRoute>} />
        <Route path="campaigns/:id" element={<LazyRoute><CampaignDetailPage /></LazyRoute>} />
        <Route path="campaigns/:id/analytics" element={<LazyRoute><CampaignAnalyticsPage /></LazyRoute>} />
        <Route path="campaigns/:id/execution" element={<LazyRoute><CampaignExecutionPage /></LazyRoute>} />
        <Route path="campaigns/:id/health" element={<LazyRoute><CampaignHealthPage /></LazyRoute>} />
        <Route path="campaigns/:id/history" element={<LazyRoute><CampaignHistoryPage /></LazyRoute>} />
        <Route path="brands" element={<LazyRoute><BrandsPage /></LazyRoute>} />
        <Route path="brands/new" element={<LazyRoute><NewBrandPage /></LazyRoute>} />
        <Route path="brands/compare" element={<LazyRoute><BrandComparisonPage /></LazyRoute>} />
        <Route path="brands/:id" element={<LazyRoute><BrandDashboardWrapper /></LazyRoute>} />
        <Route path="brands/:id/competitors" element={<LazyRoute><CompetitorsPage /></LazyRoute>} />
        <Route path="brands/:id/competitors/:competitorId" element={<LazyRoute><CompetitorDetailPage /></LazyRoute>} />
        {/* Redirect /competitive-intel to Brands page with competitive-intel tab */}
        <Route path="competitive-intel" element={<Navigate to="/brands?tab=competitive-intel" replace />} />

        {/* Multi-Brand Management - Phase 3 Epic 2 */}
        <Route path="multi-brand" element={<LazyRoute><MultiBrandDashboardPage /></LazyRoute>} />
        <Route path="analytics/comparative" element={<LazyRoute><ComparativeAnalyticsPage /></LazyRoute>} />
        <Route path="briefs" element={<LazyRoute><BriefsListPage /></LazyRoute>} />
        <Route path="briefs/new" element={<LazyRoute><NewBriefPage /></LazyRoute>} />
        <Route path="briefs/edit/:id" element={<LazyRoute><EditBriefPage /></LazyRoute>} />
        <Route path="briefs/:id" element={<LazyRoute><BriefDetailPage /></LazyRoute>} />
        <Route path="abtests" element={<LazyRoute><ABTestsPage /></LazyRoute>} />
        <Route path="abtests/:testId" element={<LazyRoute><ABTestDetailPage /></LazyRoute>} />
        <Route path="reports" element={<LazyRoute><ReportsPage /></LazyRoute>} />
        <Route path="decisions" element={<LazyRoute><DecisionsPage /></LazyRoute>} />
        <Route path="decisions/:id" element={<LazyRoute><DecisionDetailPage /></LazyRoute>} />
        <Route path="workflows" element={<LazyRoute><WorkflowsPage /></LazyRoute>} />
        <Route path="creatives" element={<LazyRoute><CreativesPage /></LazyRoute>} />
        <Route path="creatives/:id" element={<LazyRoute><CreativeDetailPage /></LazyRoute>} />
        <Route path="creatives/:id/analytics" element={<LazyRoute><CreativeAnalyticsPage /></LazyRoute>} />
        {/* Epic 12: Content Library */}
        <Route path="content" element={<LazyRoute><ContentLibraryPage /></LazyRoute>} />
        <Route path="customers" element={<LazyRoute><CustomersPage /></LazyRoute>} />
        <Route path="customers/:id" element={<LazyRoute><Customer360Page /></LazyRoute>} />
        {/* Redirect /segments to Audience page with segments tab */}
        <Route path="segments" element={<Navigate to="/customers?tab=segments" replace />} />
        <Route path="segments/:id" element={<LazyRoute><SegmentDetailPage /></LazyRoute>} />
        <Route path="settings" element={<LazyRoute><SettingsPage /></LazyRoute>} />
        <Route path="admin" element={<LazyRoute><AdminDashboardPage /></LazyRoute>} />
        <Route path="admin/feedback" element={<LazyRoute><FeedbackManagementPage /></LazyRoute>} />
      </Route>

      </Route>

      {/* 404 page — no auth needed */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App
